import { tenantApi } from '@shared/api/tenantApi';
import { unwrap, unwrapList } from '@shared/api/unwrap';
import { useAuthStore } from '@shared/stores/authStore';

/**
 * Classifieds — member routes (`/classifieds*`). Payloads match
 * ClassifiedController zod schemas (member portal parity).
 */

export type ClassifiedStatus = 'PENDING' | 'APPROVED' | 'DECLINED' | 'CLOSED';

export interface ClassifiedCategory {
  classified_category_id: string;
  name: string;
  description?: string | null;
}

export interface ClassifiedSummary {
  classified_id: string;
  member_id?: string;
  classified_category_id?: string | null;
  title: string;
  price?: number;
  currency?: string;
  category?: string;
  cover_image_url?: string;
  posted_at: string;
  author_name?: string;
  status: ClassifiedStatus;
  expiry?: string;
}

export interface ClassifiedDetail extends ClassifiedSummary {
  description: string;
  images: Array<{ id: string; url: string }>;
  show_name: boolean;
  show_email: boolean;
  show_phone: boolean;
  member_name?: string | null;
  member_email?: string | null;
  member_phone?: string | null;
  seller_email?: string | null;
  seller_phone?: string | null;
}

export interface NewClassifiedPayload {
  member_id: string;
  title: string;
  description?: string | null;
  classified_category_id?: string | null;
  price: number;
  expiry: string;
  show_name?: boolean;
  show_email?: boolean;
  show_phone?: boolean;
  seller_phone?: string | null;
  seller_email?: string | null;
}

export type UpdateClassifiedPayload = Partial<Omit<NewClassifiedPayload, 'member_id'>>;

interface RawClassified {
  classified_id: string;
  member_id?: string;
  classified_category_id?: string | null;
  title: string;
  description?: string | null;
  price?: string | number | null;
  expiry?: string;
  status?: string;
  created_at?: string;
  profile_image_path?: string | null;
  category_name?: string | null;
  member_name?: string | null;
  show_name?: boolean;
  show_email?: boolean;
  show_phone?: boolean;
  seller_email?: string | null;
  seller_phone?: string | null;
  member_email?: string | null;
  member_phone?: string | null;
}

interface RawClassifiedImage {
  image_id: string;
  image_path: string;
}

function mapStatus(status?: string): ClassifiedStatus {
  const s = (status ?? 'PENDING').toUpperCase();
  if (s === 'APPROVED' || s === 'DECLINED' || s === 'CLOSED' || s === 'PENDING') {
    return s;
  }
  return 'PENDING';
}

function mapClassifiedSummary(row: RawClassified): ClassifiedSummary {
  const priceNum =
    row.price == null || row.price === '' ? undefined : Number(row.price);
  return {
    classified_id: row.classified_id,
    member_id: row.member_id,
    classified_category_id: row.classified_category_id ?? null,
    title: row.title,
    price: Number.isFinite(priceNum) ? priceNum : undefined,
    currency: 'ZAR',
    category: row.category_name?.trim() || undefined,
    cover_image_url: row.profile_image_path ?? undefined,
    posted_at: row.created_at ?? new Date().toISOString(),
    author_name: row.member_name?.trim() || undefined,
    status: mapStatus(row.status),
    expiry: row.expiry,
  };
}

function mapClassifiedDetail(row: RawClassified, images: RawClassifiedImage[]): ClassifiedDetail {
  const base = mapClassifiedSummary(row);
  const gallery = images.map((img) => ({
    id: img.image_id,
    url: img.image_path,
  }));
  const allImages =
    gallery.length > 0
      ? gallery
      : base.cover_image_url
        ? [{ id: 'profile', url: base.cover_image_url }]
        : [];

  return {
    ...base,
    description: row.description?.trim() ?? '',
    images: allImages,
    show_name: row.show_name ?? true,
    show_email: Boolean(row.show_email),
    show_phone: Boolean(row.show_phone),
    member_name: row.member_name?.trim() || null,
    member_email: row.member_email?.trim() || null,
    member_phone: row.member_phone?.trim() || null,
    seller_email: row.seller_email?.trim() || row.member_email?.trim() || null,
    seller_phone: row.seller_phone?.trim() || row.member_phone?.trim() || null,
  };
}

export function defaultClassifiedExpiry(): string {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
}

export function currentMemberId(): string | undefined {
  const user = useAuthStore.getState().user;
  return user?.memberId ?? user?.member_id;
}

export async function fetchClassifiedCategories(): Promise<ClassifiedCategory[]> {
  const { data } = await tenantApi.get('/classifieds/categories');
  return unwrapList<ClassifiedCategory>(data);
}

/** Marketplace browse — approved listings; optional `category_id` filter. */
export async function fetchClassifieds(categoryId?: string): Promise<ClassifiedSummary[]> {
  const { data } = await tenantApi.get('/classifieds', {
    params: {
      page: 1,
      limit: 50,
      sort_by: 'created_at',
      sort_order: 'desc',
      ...(categoryId ? { category_id: categoryId } : {}),
    },
  });
  return unwrapList<RawClassified>(data).map(mapClassifiedSummary);
}

export async function fetchMyClassifieds(): Promise<ClassifiedSummary[]> {
  const { data } = await tenantApi.get('/classifieds', {
    params: {
      page: 1,
      limit: 50,
      sort_by: 'created_at',
      sort_order: 'desc',
      mine: 'true',
    },
  });
  return unwrapList<RawClassified>(data).map(mapClassifiedSummary);
}

export async function fetchClassified(classifiedId: string): Promise<ClassifiedDetail> {
  const [detailRes, imagesRes] = await Promise.all([
    tenantApi.get(`/classifieds/${classifiedId}`),
    tenantApi.get(`/classifieds/${classifiedId}/images`).catch(() => ({ data: { data: [] } })),
  ]);
  const row = unwrap<RawClassified>(detailRes.data);
  const images = unwrapList<RawClassifiedImage>(imagesRes.data);
  return mapClassifiedDetail(row, images);
}

export async function createClassified(payload: NewClassifiedPayload): Promise<ClassifiedDetail> {
  const { data } = await tenantApi.post('/classifieds', payload);
  const row = unwrap<RawClassified>(data);
  return mapClassifiedDetail(row, []);
}

export async function updateClassified(
  id: string,
  payload: UpdateClassifiedPayload,
): Promise<ClassifiedDetail> {
  const { data } = await tenantApi.put(`/classifieds/${id}`, payload);
  const row = unwrap<RawClassified>(data);
  const imagesRes = await tenantApi.get(`/classifieds/${id}/images`).catch(() => ({ data: { data: [] } }));
  const images = unwrapList<RawClassifiedImage>(imagesRes.data);
  return mapClassifiedDetail(row, images);
}

export async function deleteClassified(id: string): Promise<void> {
  await tenantApi.delete(`/classifieds/${id}`);
}

export async function uploadClassifiedProfileImage(
  classifiedId: string,
  file: Blob,
): Promise<string> {
  const formData = new FormData();
  formData.append('file', file, 'listing.jpg');
  const { data } = await tenantApi.post(`/classifieds/${classifiedId}/profile-picture`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const result = unwrap<{ profile_image_url: string }>(data);
  return result.profile_image_url;
}

export async function uploadClassifiedGalleryImage(
  classifiedId: string,
  file: Blob,
): Promise<{ id: string; url: string }> {
  const formData = new FormData();
  formData.append('file', file, 'gallery.jpg');
  const { data } = await tenantApi.post(`/classifieds/${classifiedId}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const row = unwrap<RawClassifiedImage>(data);
  return { id: row.image_id, url: row.image_path };
}

export async function deleteClassifiedGalleryImage(
  classifiedId: string,
  imageId: string,
): Promise<void> {
  await tenantApi.delete(`/classifieds/${classifiedId}/images/${imageId}`);
}
